/**
 * Frontend Module Registry
 * 
 * Provides a registry of available modules and their frontend components.
 * 
 * Each module can provide:
 * - DashboardWidget: Component shown in the dashboard module row
 * - SettingsPage: Component for module configuration
 * - ChannelPanel: Component on the channel page, below the chat (R25 chat 2)
 * - routes: Additional routes the module needs
 */

// Re-export types
export type {
  ModuleWidgetProps,
  ModuleSettingsProps,
  ChannelPanelProps,
  FrontendModule,
} from './types'

import type { FrontendModule } from './types'

/**
 * Registry of frontend modules.
 * 
 * Filled by the build-time glob at the bottom of this file (R23).
 */
const moduleRegistry: Map<string, FrontendModule> = new Map()

/**
 * Register a frontend module.
 */
export function registerModule(module: FrontendModule): void {
  moduleRegistry.set(module.name, module)
}

/**
 * Get a module by name.
 */
export function getModule(name: string): FrontendModule | undefined {
  return moduleRegistry.get(name)
}

/**
 * Get all registered modules.
 */
export function getAllModules(): FrontendModule[] {
  return Array.from(moduleRegistry.values())
}

/**
 * Check if a module has a dashboard widget.
 */
export function hasWidget(name: string): boolean {
  const module = moduleRegistry.get(name)
  return module?.DashboardWidget !== undefined
}

/**
 * Check if a module has a settings page.
 */
export function hasSettings(name: string): boolean {
  const module = moduleRegistry.get(name)
  return module?.SettingsPage !== undefined
}

// =============================================================================
// Auto-register modules
// =============================================================================

// Every folder here whose index.ts default-exports its FrontendModule is
// registered at build time (R23 P8) - no line to add per module. Eager, so the
// registry is complete before any page reads it, as it was when this was a
// hand-written import list.
const discovered = import.meta.glob<{ default?: FrontendModule }>('./*/index.ts', {
  eager: true,
})

for (const [path, mod] of Object.entries(discovered)) {
  if (mod.default) {
    registerModule(mod.default)
  } else {
    console.warn(`[modules] ${path} has no default export; not registered`)
  }
}
